import React, { useCallback, useEffect, useMemo, useState } from "react";

import moment, { Moment } from "moment";

import {
  getDatePickerDisabledRange,
  useGetFarms,
  useGetPracticeCrops,
  useGetPracticeSeedings,
  useGetRedzoneExceptions,
  usePracticeManagementCropParameter,
  useTranslation,
} from "utils";

import { CropResidueType, HarvestUnitValue } from "consts";

import {
  ACCOUNTING_METHODOLOGY,
  CROP_TYPE_FCPO,
  CropFormValidationError,
  OTHER_REDZONE_EXCEPTION_KEY,
  PracticeCrop,
  RedzoneException,
  WithInjectedName,
  ZERO_UUID,
} from "models";

import {
  AlertBanner,
  Checkbox,
  Flex,
  Form,
  KlimRadioGroup,
  OptGroup,
  Option,
  RangePicker,
  RangerPickerValueType,
  Select,
  Spin,
  TextArea,
  Typography,
} from "components";

import { BulkEditCrop } from "pages/app/all-field-view/components/BulkEditCropDrawer.tsx";
import { getFormTitle } from "pages/app/all-field-view/components/forms/crop/AddEditCropForm.tsx";
import { CropYieldSection } from "pages/app/all-field-view/components/forms/crop/CropYieldSection.tsx";
import { SecondaryDataCropForm } from "pages/app/all-field-view/components/forms/crop/SecondaryDataCropForm.tsx";
import { CALENDAR_YEAR_END, CALENDAR_YEAR_START, CROP, FARM_YEAR_START } from "pages/app/all-field-view/constants/cropForm.ts";
import { getSharedSecondaryDataAnswer, isSecondaryDataQuestionShown } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";
import {
  BulkAddEditCropFormProps,
  getCropSelectionOptions,
  getRedZoneQuestionInfoForForm,
  getResidueInfo,
  getSpeciesCountInfo,
  getYieldInfo,
} from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import {
  InputRequirementConfig,
  getDiverseCoverCropIncentive,
  isRequired,
  isRequiredForSaving,
  shouldShowInput,
} from "pages/app/all-field-view/utils/getInputRequirementConfig.ts";

export type BulkEditCropFormProps = {
  selectedItems: BulkEditCrop[];
  inputRequirementConfigPerFieldAndCrop: Array<InputRequirementConfig | null>;
  optionalInputsAreShown: boolean;
  cropPeriodValidationErrors: CropFormValidationError[];
  disableTillageAndSeedingInputs: boolean;
  year: number;
  regionId?: string;
  recommendedSeedingDate: Moment;
  recommendedHarvestDate: Moment;
  onCropSelectionChanged: (cropId: string, year: number, cropType: CROP_TYPE_FCPO) => void;
  isInRedzone: boolean;
};

export const BulkEditCropForm = ({
  selectedItems,
  inputRequirementConfigPerFieldAndCrop,
  optionalInputsAreShown,
  cropPeriodValidationErrors,
  disableTillageAndSeedingInputs,
  regionId,
  year,
  recommendedSeedingDate,
  recommendedHarvestDate,
  onCropSelectionChanged,
  isInRedzone,
}: BulkEditCropFormProps) => {
  const { t } = useTranslation("allFieldView");
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const { currentFarm } = useGetFarms();
  const cropPracticesQuery = useGetPracticeCrops({ regionId: regionId, version: currentFarm?.carbonProgram?.farmLevelFootprinting?.methodology?.version });
  const form = Form.useFormInstance<BulkAddEditCropFormProps>();
  const allNonePlaceholderCrops = selectedItems.filter((selectedItem) => !selectedItem.isPlaceholder);

  // Consts
  const currentYear = moment().year();
  const { cropType } = selectedItems[0];

  // Crop
  const [cropsToDisplay, setCropsToDisplay] = useState<Array<WithInjectedName<PracticeCrop>> | undefined>([]);
  const [selectedCropId, setSelectedCropId] = useState<string | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<WithInjectedName<PracticeCrop> | null>(null);
  const [isAnyCropConfirmed, setIsAnyCropConfirmed] = useState<boolean>(false);
  const [allSameCrops, setAllSameCrops] = useState<boolean>(false);
  const initialOnlyPlaceholderCrops = selectedItems.every((item) => item.isPlaceholder);
  const [onlyPlaceholderCrops, setOnlyPlaceholderCrops] = useState<boolean>(initialOnlyPlaceholderCrops);
  const [showCropInput, setShowCropInput] = useState<boolean>(true);
  const [isCropRequiredForSaving, setIsCropRequiredForSaving] = useState<boolean>(true);
  const isConfirmed = selectedItems.some((item) => item.isConfirmed);

  // Date
  const [showDateInput, setShowDateInput] = useState<boolean>(true);
  const [isDateRequiredForSaving, setIsDateRequiredForSaving] = useState<boolean>(true);
  const [hasMixedDates, setHasMixedDates] = useState<boolean>(true);
  const [seedingDates, setSeedingDates] = useState<RangerPickerValueType>([null, null]);
  const [harvestDates, setHarvestDates] = useState<RangerPickerValueType>([null, null]);

  // Residue
  const [isResidueProduced, setIsResidueProduced] = useState<boolean | null>(() => null);
  const [hasMixedResidue, setHasMixedResidue] = useState<boolean>();
  const [showResidue, setShowResidue] = useState<boolean>(false);
  const [isResidueRequired, setIsResidueRequired] = useState<boolean>();
  const [residueType, setResidueType] = useState<CropResidueType | null>();
  const [residueNote, setResidueNote] = useState<string | null>();
  const [hasMixedResidueNotes, setHasMixedResidueNotes] = useState<boolean>();

  // Yield
  const [appliedYield, setAppliedYield] = useState<number | null>(null);
  const [appliedYieldUnit, setAppliedYieldUnit] = useState<HarvestUnitValue | null>(null);
  const [hasMixedYield, setHasMixedYield] = useState<boolean>(false);

  const [showYieldInput, setShowYieldInput] = useState<boolean>(true);
  const [isYieldRequired, setIsYieldRequired] = useState<boolean>();
  const [_isYieldRequiredForSaving, setIsYieldRequiredForSaving] = useState<boolean>();
  const [committedYield, setCommittedYield] = useState<number | null>(null);
  const [committedYieldUnit, setCommittedYieldUnit] = useState<HarvestUnitValue | null>(null);

  // Seeding
  const [selectedSeedingType, setSelectedSeedingType] = useState<string | null>(null);
  const [allSameSeeding, setAllSameSeeding] = useState<boolean>(false);
  const [hasExistingSeeding, setHasExistingSeeding] = useState<boolean>(false);
  const [showSeedingInput, setShowSeedingInput] = useState<boolean>(true);
  const [isSeedingRequiredForSaving, setIsSeedingRequiredForSaving] = useState<boolean>(true);

  // Species count
  const diverseCoverCropIncentive = useMemo(() => getDiverseCoverCropIncentive(currentFarm?.carbonProgram), [currentFarm?.carbonProgram]);
  const speciesCountThreshold = diverseCoverCropIncentive?.speciesCountThreshold;
  const [showSpeciesCountInput, setShowSpeciesCountInput] = useState<boolean>(false);
  const [speciesCountChecked, setSpeciesCountChecked] = useState<boolean>(false);
  const [hasMixedSpeciesCount, setHasMixedSpeciesCount] = useState<boolean>(false);

  // Red Zones
  const { data: redzoneExceptions } = useGetRedzoneExceptions();
  const {
    redZoneQuestion,
    redZoneExceptionAnswer: redzoneExceptionId,
    hasMixedRedZoneQuestion,
    hasMixedRedZoneAnswer,
    hasMixedRedZoneExplanation,
    redZoneExceptionExplanation,
    isAnswerRequiredForSaving: initialIsAnswerRequiredForSaving,
    isQuestionRequiredForSaving,
  } = getRedZoneQuestionInfoForForm(selectedItems);
  const [mandatory, setMandatory] = useState<boolean | null>(redZoneQuestion);
  const [isAnswerRequiredForSaving, setIsAnswerRequiredForSaving] = useState<boolean>(initialIsAnswerRequiredForSaving);
  const [selectedRedzoneExceptionId, setSelectedRedzoneExceptionId] = useState<string | null>(redzoneExceptionId);

  const isIntervention = currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.intervention;

  const cropParameterQuery = usePracticeManagementCropParameter(selectedCrop?.id ?? "");
  const showSecondaryDataForm = isSecondaryDataQuestionShown(selectedItems, currentFarm?.carbonProgram?.accountingMethodology);

  // Crop helpers
  const getCropsIncludingPrevSelectedDisabledCrop = () => {
    const crops = cropPracticesQuery.data?.filter((crop) => crop.enabled);
    // Add disabled crops to the crop selection if they have been selected previously by the user
    selectedItems.forEach((selectedCrop) => {
      const foundCrop = cropPracticesQuery.data?.find((crop) => crop.id === selectedCrop.cropId);
      if (!foundCrop || foundCrop.enabled) return;
      crops?.push(foundCrop);
    });

    return crops;
  };

  const defineInputRequirements = (inputRequirementConfigs: Array<InputRequirementConfig | null>) => {
    // Crops
    const showCropInput = inputRequirementConfigs.some((inputRequirementConfig) => shouldShowInput(inputRequirementConfig?.cropName, optionalInputsAreShown));
    const isCropRequiredForSaving = inputRequirementConfigs.some((inputRequirementConfig) => isRequiredForSaving(inputRequirementConfig?.cropName));
    setShowCropInput(showCropInput);
    setIsCropRequiredForSaving(isCropRequiredForSaving);
    // Dates
    const showDateInput = inputRequirementConfigs.some((inputRequirementConfig) => shouldShowInput(inputRequirementConfig?.dates, optionalInputsAreShown));
    const isDateRequiredForSaving = inputRequirementConfigs.some((inputRequirementConfig) => isRequired(inputRequirementConfig?.dates));
    setShowDateInput(showDateInput);
    setIsDateRequiredForSaving(isDateRequiredForSaving);
    // Yield
    const showYieldInput = inputRequirementConfigs.some((inputRequirementConfig) => shouldShowInput(inputRequirementConfig?.yield, optionalInputsAreShown));
    const isYieldRequiredForSaving = inputRequirementConfigs.some((inputRequirementConfig) => isRequiredForSaving(inputRequirementConfig?.yield));
    const isYieldRequired = inputRequirementConfigs.some((inputRequirementConfig) => isRequired(inputRequirementConfig?.yield));
    setShowYieldInput(showYieldInput);
    setIsYieldRequiredForSaving(isYieldRequiredForSaving);
    setIsYieldRequired(isYieldRequired);
    // Seeding
    const showSeedingInput = inputRequirementConfigs.some((inputRequirementConfig) =>
      shouldShowInput(inputRequirementConfig?.seedings, optionalInputsAreShown)
    );
    const isSeedingRequiredForSaving = inputRequirementConfigs.some((inputRequirementConfig) => isRequiredForSaving(inputRequirementConfig?.seedings));
    setShowSeedingInput(showSeedingInput);
    setIsSeedingRequiredForSaving(isSeedingRequiredForSaving);
    // Species count
    const showSpeciesCountInput = inputRequirementConfigs.some((inputRequirementConfig) => shouldShowInput(inputRequirementConfig?.speciesCount, false));
    setShowSpeciesCountInput(showSpeciesCountInput);
  };

  // Date helpers
  const isCropPartiallyEditable = useMemo(() => {
    const firstCrop = selectedItems.find((crop) => !crop.isPlaceholder);
    const harvestEnd = firstCrop?.harvestEndDate ?? firstCrop?.fallbackDates.harvestEndDate;
    if (!harvestEnd) return false;

    const savedHarvestYear = moment(harvestEnd).year();
    const isHarvestInCurrentYear = savedHarvestYear === currentYear;

    return cropType !== CROP_TYPE_FCPO.UNDERSOWING && isHarvestInCurrentYear && isAnyCropConfirmed;
  }, [selectedItems, currentYear, cropType, isAnyCropConfirmed]);

  const getDatePickerDisabledDate = useCallback(
    (current: moment.Moment, isSeedingDate: boolean): boolean => {
      const firstCrop = selectedItems[0];

      if (!firstCrop) return true;
      if (!isAnyCropConfirmed || !firstCrop.year) return false;

      const reportingPeriodStart = currentFarm?.carbonProgram?.fieldsManagement.reportingPeriod
        ? `${currentFarm?.carbonProgram?.fieldsManagement.reportingPeriod?.start}/${firstCrop.year - 1}`
        : `${CALENDAR_YEAR_START}${firstCrop.year}`;

      const reportingPeriodEnd = currentFarm?.carbonProgram?.fieldsManagement.reportingPeriod
        ? `${currentFarm?.carbonProgram?.fieldsManagement.reportingPeriod?.end}/${firstCrop.year}`
        : `${CALENDAR_YEAR_END}${firstCrop.year}`;

      // Crops in inventory programs and partially editable crops can only be moved within the reporting period
      if (currentFarm?.carbonProgram?.accountingMethodology === ACCOUNTING_METHODOLOGY.inventory || isCropPartiallyEditable) {
        return getDatePickerDisabledRange({ current, startDate: reportingPeriodStart, endDate: reportingPeriodEnd });
      }

      // Seeding dates of verified main crops can be changed within beginning of farm year and end of reporting period.
      if (firstCrop.cropType === CROP_TYPE_FCPO.MAIN && isSeedingDate) {
        return getDatePickerDisabledRange({ current, startDate: `${FARM_YEAR_START}${firstCrop.year - 1}`, endDate: reportingPeriodEnd });
      }

      // Harvest dates of verified main crops can only be changed within the reporting period.
      if (firstCrop.cropType === CROP_TYPE_FCPO.MAIN && !isSeedingDate) {
        return getDatePickerDisabledRange({ current, startDate: reportingPeriodStart, endDate: reportingPeriodEnd });
      }

      // Harvest dates of verified undersowing and cover crops can be changed as long as they are past the seeding date.
      if (!isSeedingDate) {
        return current.isBefore(moment(firstCrop.seedingStartDate ?? firstCrop.fallbackDates.seedingStartDate ?? reportingPeriodStart), "day");
      }

      // Seeding dates of verified undersowing and cover crops cannot be changed anymore and are therefore disabled.
      return true;
    },
    [isCropPartiallyEditable, isAnyCropConfirmed, selectedItems, currentFarm?.carbonProgram]
  );

  const setInitialDateRanges = () => {
    const nonePlaceholderCrops = selectedItems.filter((crop) => !crop.isPlaceholder);
    if (onlyPlaceholderCrops || nonePlaceholderCrops.length === 0) {
      setHasMixedDates(false);
      return;
    }

    setHasMixedDates(true);
    const referenceCrop = nonePlaceholderCrops[0];

    const allSameSeedingStart = nonePlaceholderCrops.every((crop) => crop.seedingStartDate === referenceCrop.seedingStartDate);
    const allSameSeedingEnd = nonePlaceholderCrops.every((crop) => crop.seedingEndDate === referenceCrop.seedingEndDate);
    const allSameHarvestStart = nonePlaceholderCrops.every((crop) => crop.harvestStartDate === referenceCrop.harvestStartDate);
    const allSameHarvestEnd = nonePlaceholderCrops.every((crop) => crop.harvestEndDate === referenceCrop.harvestEndDate);

    const seedingStart = allSameSeedingStart && referenceCrop.seedingStartDate ? moment(referenceCrop.seedingStartDate) : null;
    const seedingEnd = allSameSeedingEnd && referenceCrop.seedingEndDate ? moment(referenceCrop.seedingEndDate) : null;
    const harvestStart = allSameHarvestStart && referenceCrop.harvestStartDate ? moment(referenceCrop.harvestStartDate) : null;
    const harvestEnd = allSameHarvestEnd && referenceCrop.harvestEndDate ? moment(referenceCrop.harvestEndDate) : null;

    setSeedingDates([seedingStart, seedingEnd]);
    setHarvestDates([harvestStart, harvestEnd]);
    form.setFieldsValue({ crop: { seedingDateRange: [seedingStart, seedingEnd] } });
    form.setFieldsValue({ crop: { harvestDateRange: [harvestStart, harvestEnd] } });

    const hasSameDates = allSameSeedingStart && allSameSeedingEnd && allSameHarvestStart && allSameHarvestEnd;
    setHasMixedDates(!hasSameDates);
  };

  const handleMandatoryRedZoneQuestion = (isMandatory: boolean) => {
    // When a user changes mandatory to false manually, we must require an answer
    setIsAnswerRequiredForSaving(!isMandatory);
    setMandatory(isMandatory);
    if (isMandatory) {
      form.setFieldValue([CROP, "redzoneExceptionExplanation"], undefined);
    }
  };

  const isOtherRedzoneException = (exceptionId: string | null) =>
    !!redzoneExceptions?.some((option) => option.id === exceptionId && option.key === OTHER_REDZONE_EXCEPTION_KEY);

  const isOtherRedzoneExceptionSelected = mandatory === false && isOtherRedzoneException(selectedRedzoneExceptionId);

  // Handle Crop
  useEffect(() => {
    const crops = getCropsIncludingPrevSelectedDisabledCrop();
    setCropsToDisplay(crops);

    const anyCropConfirmed = !!selectedItems.find((crop) => crop.isConfirmed);
    setIsAnyCropConfirmed(anyCropConfirmed);

    const onlyPlaceholder = selectedItems.every((selectedItem) => selectedItem.isPlaceholder);
    setOnlyPlaceholderCrops(onlyPlaceholder);
    if (onlyPlaceholder) return;

    const isSameCrop = allNonePlaceholderCrops.every((item, index, array) => item.cropId && item.cropId === array[0].cropId);
    setAllSameCrops(isSameCrop);
    if (isSameCrop) {
      const randomCropsId = allNonePlaceholderCrops[0].cropId;
      const selectedCrop = crops?.find((crop) => crop.id === randomCropsId);
      if (selectedCrop) {
        setSelectedCropId(selectedCrop.id);
      }
    }
  }, [cropPracticesQuery.data, selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const selectedCrop = cropsToDisplay?.find((crop) => crop.id === selectedCropId);
    // We need to set the form value for the cropId whenever selectedCropId changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.

    form.setFieldsValue({ crop: { cropId: selectedCropId } });
    setSelectedCrop(selectedCrop ?? null);
  }, [selectedCropId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedCrop && !selectedCrop.isPerennial && form.getFieldValue([CROP, "seedingPracticeType"]) === ZERO_UUID) {
      form.setFieldValue([CROP, "seedingPracticeType"], null);
    }
  }, [selectedCrop]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Dates
  useEffect(() => {
    setInitialDateRanges();
  }, [onlyPlaceholderCrops, selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle residue
  useEffect(() => {
    if (!cropsToDisplay || cropsToDisplay.length === 0) return;
    const { showResidue, isRequired, hasMixedResidue, isResidueProduced, residueType, residueNote, hasMixedResidueNotes } = getResidueInfo({
      selectedItems,
      cropsToDisplay,
      selectedCropId,
      onlyPlaceholderCrops,
    });
    setShowResidue(showResidue);
    setHasMixedResidue(hasMixedResidue);
    setIsResidueProduced(isResidueProduced);
    setIsResidueRequired(isRequired);
    setResidueType(residueType);
    setResidueNote(residueNote);
    setHasMixedResidueNotes(hasMixedResidueNotes);
  }, [selectedItems, selectedCropId, cropsToDisplay, onlyPlaceholderCrops]);

  // Handle species count
  useEffect(() => {
    const { defaultChecked, hasMixedSpeciesCount } = getSpeciesCountInfo({
      selectedItems,
      selectedCropId,
      threshold: speciesCountThreshold,
      onlyPlaceholderCrops,
    });
    setSpeciesCountChecked(defaultChecked);
    setHasMixedSpeciesCount(hasMixedSpeciesCount);
  }, [selectedItems, selectedCropId, speciesCountThreshold, onlyPlaceholderCrops]);

  // Handle yield
  useEffect(() => {
    const { valueToDisplay, hasMixedYieldValues: hasMixedValue, valueUnit } = getYieldInfo({ selectedItems, onlyPlaceholderCrops });
    form.setFieldsValue({ crop: { yieldPerHa: valueToDisplay, yieldPerHaUnit: valueUnit } });

    setAppliedYield(valueToDisplay);
    setAppliedYieldUnit(valueUnit);
    setCommittedYield(valueToDisplay);
    setCommittedYieldUnit(valueUnit);
    setHasMixedYield(hasMixedValue);
  }, [selectedItems, onlyPlaceholderCrops]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle wasHarvested
  useEffect(() => {
    if (allNonePlaceholderCrops.length === 0) return;
    const firstHarvested = allNonePlaceholderCrops[0].wasHarvested ?? true;
    const allSame = allNonePlaceholderCrops.every((i) => (i.wasHarvested ?? true) === firstHarvested);
    form.setFieldsValue({ crop: { wasHarvested: allSame ? firstHarvested : true } });
  }, [selectedItems]); // eslint-disable-line react-hooks/exhaustive-deps

  const isZeroYieldsAllowed = cropsToDisplay?.find((c) => c.id === selectedCropId)?.harvestIsMandatory === false;
  const showSpeciesCountCheckbox = showSpeciesCountInput && !!selectedCropId && !!diverseCoverCropIncentive?.elementIds?.includes(selectedCropId);

  // Handle Input Requirements
  useEffect(() => {
    defineInputRequirements(inputRequirementConfigPerFieldAndCrop);
  }, [inputRequirementConfigPerFieldAndCrop, optionalInputsAreShown]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle Seeding
  useEffect(() => {
    const allSameSeeding = allNonePlaceholderCrops.every((crop, _, array) => crop.seedingPracticeType === array[0].seedingPracticeType);
    setAllSameSeeding(allSameSeeding);
    if (!selectedItems.find((crop) => crop.seedingPracticeType != null && crop.seedingPracticeType.trim() !== "")) {
      setHasExistingSeeding(false);
      return;
    }
    setHasExistingSeeding(true);
    if (allSameSeeding && selectedItems.length > 0) {
      const { seedingPracticeType } = selectedItems[0];
      const seedingType = (seedingPracticeType === ZERO_UUID && !selectedCrop?.isPerennial ? t("fieldTimeline:cropCard.unknown") : seedingPracticeType) || null;
      setSelectedSeedingType(seedingType);
    }
  }, [selectedItems, selectedSeedingType, selectedCrop]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // We need to set the form value for the seedingPracticeType whenever selectedSeedingType changes.
    // Since defaultValue is only used for the initial mount and does not update dynamically.

    form.setFieldsValue({ crop: { seedingPracticeType: selectedSeedingType } });
  }, [selectedSeedingType]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCropSelectionChanged = (cropId: string) => {
    setSelectedCropId(cropId);

    onCropSelectionChanged(cropId, year, cropType);
  };

  // Update form value when showResidue changes: the form value is initialized by a hidden form item, so we need to update it manually to keep it in sync with the state
  useEffect(() => {
    form.setFieldsValue({ crop: { showResidue } });
  }, [form, showResidue]);

  useEffect(() => {
    form.setFieldsValue({ crop: { showSpeciesCount: showSpeciesCountCheckbox } });
  }, [form, showSpeciesCountCheckbox]);

  return (
    <Flex vertical gap="1rem" className="add-edit-crop-form-container">
      {selectedItems.some((item) => item.isConfirmed) && (
        <Typography variant="paragraph-m" className="submitted-crop-message">
          {t("cropForm.submittedCropMessage")}
        </Typography>
      )}
      {/* If everything is confirmed as "not documented" i.e. is secondary data, pre-fill. Otherwise, leave blank */}
      {showSecondaryDataForm && <SecondaryDataCropForm initialValue={getSharedSecondaryDataAnswer(selectedItems)} />}
      <Flex className="add-edit-crop-form">
        <div className="form-container" data-testid="add-edit-crop-form">
          <Flex justify="space-between">
            <Flex gap="1rem">
              <Typography variant="h5" className="margin-bottom-100">
                {t(getFormTitle(cropType))}
              </Typography>
            </Flex>
          </Flex>
          <Form.Item name={[CROP, "cropType"]} hidden={true} initialValue={cropType}>
            {/* we need this to prevent an antd form error; Form.Item needs a valid child */}
            <div />
          </Form.Item>

          {showCropInput && (
            <div className="margin-bottom-200">
              <Select
                dataTestId="add-edit-crop-form-top-crop-selection"
                label={onlyPlaceholderCrops || allSameCrops ? t("cropForm.cropName") : t("cropForm.multipleValues")}
                name={[CROP, "cropId"]}
                initialValue={selectedCropId ?? null}
                onChange={handleCropSelectionChanged}
                showSearch
                disabled={isAnyCropConfirmed}
                isLoading={cropPracticesQuery.isFetching}
                rules={[
                  {
                    // Only needed if all crops are placeholders with mixed crops, users may edit other props without changing crops
                    required: onlyPlaceholderCrops ? isCropRequiredForSaving : false,
                    message: t("cropForm.errorMessages.cropRequired"),
                  },
                ]}
              >
                {getCropSelectionOptions(cropType, cropsToDisplay).map((option, index) => (
                  <Option data-testid={`crop-selection-option-${cropType.toLowerCase()}-${index}`} key={option.id} value={option.id}>
                    {option.name}
                  </Option>
                ))}
              </Select>
            </div>
          )}

          {showDateInput && (
            <Flex gap="1rem" className="margin-top-100 margin-bottom-100">
              <Flex
                vertical
                gap="1rem"
                className="date-picker-container"
                aria-disabled={!isCropPartiallyEditable}
                data-testid="seeding-date-range-picker-container"
                data-crop-type={cropType}
              >
                <RangePicker
                  key={recommendedSeedingDate.toISOString()}
                  data-field-id="seedingDateRange"
                  dataTestId={"add-edit-crop-form-seeding-date-range-bulk-edit"}
                  initialValue={seedingDates}
                  defaultPickerValue={[recommendedSeedingDate, recommendedSeedingDate]}
                  format={["DD.MM.YYYY"]}
                  label={t("cropForm.seedingDateRange")}
                  name={[CROP, "seedingDateRange"]}
                  rules={[
                    {
                      validator: (_, value: moment.Moment[] | null) => {
                        if (!onlyPlaceholderCrops || !isDateRequiredForSaving) return Promise.resolve();
                        if (value?.[0] != null) return Promise.resolve();
                        return Promise.reject(new Error(t("cropForm.errorMessages.seedingDateRequired")));
                      },
                    },
                  ]}
                  status={cropPeriodValidationErrors.find((error) => error.fieldId === "seedingStartDate") ? "error" : undefined}
                  // Once submitted, the date can still be changed within the respective farming year.
                  disabledDate={(current) => getDatePickerDisabledDate(current, true)}
                  help={hasMixedDates && cropPeriodValidationErrors.length === 0 ? t("cropForm.mixedValues") : undefined}
                />
              </Flex>
              <Flex
                vertical
                gap="1rem"
                className="date-picker-container"
                aria-disabled={!isCropPartiallyEditable}
                data-testid="harvest-date-range-picker-container"
                data-crop-type={cropType}
              >
                <RangePicker
                  key={recommendedHarvestDate.toISOString()}
                  data-field-id="harvestDateRange"
                  dataTestId={"add-edit-crop-form-harvest-date-range--bulk-edit"}
                  format={["DD.MM.YYYY"]}
                  initialValue={harvestDates}
                  defaultPickerValue={[recommendedHarvestDate, recommendedHarvestDate]}
                  name={[CROP, "harvestDateRange"]}
                  label={t("cropForm.harvestDateRange")}
                  rules={[
                    {
                      validator: (_, value: moment.Moment[] | null) => {
                        if (!onlyPlaceholderCrops || !isDateRequiredForSaving) return Promise.resolve();
                        if (value?.[0] != null) return Promise.resolve();
                        return Promise.resolve();
                      },
                    },
                  ]}
                  status={cropPeriodValidationErrors.find((error) => error.fieldId === "harvestEndDate") ? "error" : undefined}
                  // Once submitted, the date can still be changed within the respective farming year.
                  disabledDate={(current) => getDatePickerDisabledDate(current, false)}
                  help={hasMixedDates && cropPeriodValidationErrors.length === 0 ? t("cropForm.mixedValues") : undefined}
                />
              </Flex>
            </Flex>
          )}

          {/*  Save this to check if residue is still required after crop changes. If not, reset the multi-selected CropFCPOs residue value to null on submitting */}
          <Form.Item name={[CROP, "showResidue"]} hidden={true} initialValue={showResidue}>
            {/* we need this to prevent an antd form error; Form.Item needs a valid child */}
            <div />
          </Form.Item>

          {/*  Save this to check if the crop still qualifies for the diverse cover-crop incentive after crop changes. If not, reset the multi-selected CropFCPOs species count value to null on submitting */}
          <Form.Item name={[CROP, "showSpeciesCount"]} hidden={true} initialValue={showSpeciesCountCheckbox}>
            {/* we need this to prevent an antd form error; Form.Item needs a valid child */}
            <div />
          </Form.Item>

          {(showSeedingInput || hasExistingSeeding) && (
            <>
              {seedingPracticesQuery.isFetching ? (
                <Flex justify="center">
                  <Spin />
                </Flex>
              ) : (
                <>
                  <Select
                    dataTestId="add-edit-crop-form-seeding-type-selection"
                    disabled={disableTillageAndSeedingInputs}
                    initialValue={selectedSeedingType}
                    label={onlyPlaceholderCrops || allSameSeeding ? t("cropForm.seedingType") : t("cropForm.multipleValues")}
                    labelRender={({ label, value }) => (value === ZERO_UUID ? t("practices:seedings.none") : label)}
                    name={[CROP, "seedingPracticeType"]}
                    rules={[
                      {
                        // Only needed if all crops are placeholders with mixed crops, users may edit other props without changing crops
                        required: onlyPlaceholderCrops ? isSeedingRequiredForSaving : false,
                      },
                    ]}
                    showSearch
                    value={selectedSeedingType}
                  >
                    <OptGroup>
                      {seedingPracticesQuery.data &&
                        seedingPracticesQuery.data
                          .filter((practice) => selectedCrop?.isPerennial || practice.id !== ZERO_UUID)
                          .sort((a, b) => a.name?.localeCompare(b.name))
                          .map((option, index) => (
                            <Option data-testid={`seeding-selection-option-${index}`} key={option.id} value={option.id}>
                              {option.name}
                            </Option>
                          ))}
                    </OptGroup>
                  </Select>
                </>
              )}
            </>
          )}
          {showSpeciesCountCheckbox && (
            <Form.Item
              className="margin-top-50"
              name={[CROP, "speciesCount"]}
              initialValue={speciesCountChecked}
              valuePropName="checked"
              help={hasMixedSpeciesCount ? t("cropForm.mixedValues") : undefined}
            >
              <Checkbox>
                <Typography variant="paragraph-m">{t("cropForm.speciesCountThreshold", { count: speciesCountThreshold })}</Typography>
              </Checkbox>
            </Form.Item>
          )}
          {isIntervention && isInRedzone && cropType === CROP_TYPE_FCPO.COVER && (
            <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
              <Typography variant="paragraph-m">{t("cropForm.cropMandatory")}</Typography>
              <Form.Item
                name={[CROP, "mandatory"]}
                // Redzone inputs should always be required for saving if rendered
                rules={[{ required: isQuestionRequiredForSaving, message: t("modules:bauEntryDrawer.crops.residueLeft.error") }]}
                initialValue={mandatory}
                help={hasMixedRedZoneQuestion ? t("cropForm.mixedValues") : undefined}
              >
                <KlimRadioGroup
                  className="radio-bg-white"
                  size="large"
                  type="tertiary"
                  dataTestId="crop-mandatory-result-toggle"
                  options={[
                    {
                      value: true,
                      label: (
                        <Flex vertical justify="space-around">
                          <Typography variant="paragraph-m">{t("cropForm.yes")}</Typography>
                        </Flex>
                      ),
                    },
                    {
                      value: false,
                      label: (
                        <Flex vertical justify="space-around">
                          <Typography variant="paragraph-m">{t("cropForm.no")}</Typography>
                        </Flex>
                      ),
                    },
                  ]}
                  value={mandatory}
                  onChange={(e) => handleMandatoryRedZoneQuestion(e.target.value as boolean)}
                  optionType="button"
                  buttonStyle="solid"
                  fullWidth
                  disabled={isConfirmed}
                />
              </Form.Item>
            </Flex>
          )}
          {isIntervention && isInRedzone && cropType === CROP_TYPE_FCPO.COVER && mandatory === false && (
            <Select
              dataTestId="add-edit-crop-form-redzone-exception-type-selection"
              disabled={isConfirmed}
              initialValue={redzoneExceptionId}
              label={hasMixedRedZoneAnswer ? t("cropForm.multipleValues") : t("cropForm.whyNotMandatory")}
              name={[CROP, "redzoneExceptionId"]}
              // Redzone inputs should always be required for saving if rendered
              rules={[{ message: "", required: isAnswerRequiredForSaving }]}
              showSearch
              onChange={(value) => {
                setSelectedRedzoneExceptionId(value as string);
                if (!isOtherRedzoneException(value as string)) {
                  form.setFieldValue([CROP, "redzoneExceptionExplanation"], undefined);
                }
              }}
            >
              <OptGroup>
                {redzoneExceptions?.map((option: RedzoneException, index: number) => (
                  <Option data-testid={`crop-selection-option-redzone-${index}`} key={option.id} value={option.id}>
                    {option.description}
                  </Option>
                ))}
              </OptGroup>
            </Select>
          )}
          {isIntervention && isInRedzone && cropType === CROP_TYPE_FCPO.COVER && mandatory === false && isOtherRedzoneExceptionSelected && (
            <TextArea
              className="margin-top-100"
              dataTestId="add-edit-crop-form-redzone-exception-explanation"
              name={[CROP, "redzoneExceptionExplanation"]}
              initialValue={redZoneExceptionExplanation}
              label={t("cropForm.otherReasonExplanation")}
              rules={[{ required: true, message: t("cropForm.otherReasonExplanationRequired") }]}
              help={hasMixedRedZoneExplanation ? t("cropForm.mixedValues") : undefined}
              autoSize
              disabled={isConfirmed}
            />
          )}
        </div>
      </Flex>
      {(showYieldInput || appliedYield) && (
        <CropYieldSection
          label={t("cropForm.yield")}
          labelClassName={isYieldRequired ? "required" : ""}
          initialValue={appliedYield}
          initialValueUnit={appliedYieldUnit}
          initialIsHarvested={true}
          disabled={isConfirmed}
          isRequired={isYieldRequired ?? true}
          isZeroYieldsAllowed={isZeroYieldsAllowed}
          help={hasMixedYield && appliedYield === null ? t("cropForm.mixedValues") : undefined}
          onChange={(value, unit) => {
            setCommittedYield(value ?? null);
            setCommittedYieldUnit((unit ?? null) as HarvestUnitValue | null);
          }}
          committedYield={committedYield}
          committedYieldUnit={committedYieldUnit}
          yieldBenchmarkBoundsTperHa={cropParameterQuery.data?.harvestTonsPerHectare}
          cropName={selectedCrop?.name ?? null}
          initialNoteYield={allNonePlaceholderCrops[0]?.sanityNoteYield ?? null}
        />
      )}
      {/* The residue input does not depend on the requirement configuration because it depends directly on which crop is selected */}
      {showResidue &&
        (() => {
          const activeResidueType = selectedCrop?.residueType ?? residueType;
          return (
            <Flex className="add-edit-crop-form">
              <div className="form-container" data-testid="add-edit-crop-form-residues">
                <Typography variant="h5" className="margin-bottom-100">
                  {t(activeResidueType ? `cropForm.residue.${activeResidueType}.title` : "cropForm.residue.title")}
                </Typography>
                <Flex justify="space-between" gap="1rem" className="margin-top-100 margin-bottom-100">
                  <Typography variant="paragraph-m">
                    {cropType === CROP_TYPE_FCPO.COVER || cropType === CROP_TYPE_FCPO.UNDERSOWING
                      ? t("cropForm.residue.residueLeftSideCrop")
                      : t(`cropForm.residue.${activeResidueType ? `${activeResidueType}.` : ""}residueLeft`)}
                    *
                  </Typography>
                  <Form.Item
                    name={[CROP, "hasResidue"]}
                    // Residue should always be required for saving
                    rules={[
                      {
                        required: isResidueRequired,
                        message: t("modules:bauEntryDrawer.crops.residueLeft.error"),
                      },
                    ]}
                    initialValue={isResidueProduced}
                    help={hasMixedResidue && isResidueProduced === null ? t("cropForm.mixedValues") : undefined}
                  >
                    <KlimRadioGroup
                      className="radio-bg-white"
                      size="large"
                      type="tertiary"
                      dataTestId="residue-result-toggle"
                      options={[
                        {
                          value: true,
                          label: t("cropForm.yes"),
                        },
                        {
                          value: false,
                          label: t("cropForm.no"),
                        },
                      ]}
                      value={isResidueProduced || false}
                      onChange={(e) => setIsResidueProduced(e.target.value)}
                      optionType="button"
                      buttonStyle="solid"
                      fullWidth
                      disabled={isAnyCropConfirmed}
                    />
                  </Form.Item>
                </Flex>
                {selectedCrop && selectedCrop.residueLeftByDefault !== null && selectedCrop.residueLeftByDefault !== isResidueProduced && (
                  <Flex vertical gap={16}>
                    <AlertBanner
                      type="warning"
                      iconName="tabler/alert-triangle"
                      iconAlign="start"
                      title={t("cropForm.residue.warningTitle")}
                      description={t(
                        selectedCrop.residueType ? `cropForm.residue.${selectedCrop.residueType}.warningDescription` : "cropForm.residue.warningDescription",
                        {
                          cropName: selectedCrop.name,
                          value: isResidueProduced ? t("cropForm.yes") : t("cropForm.no"),
                        }
                      )}
                      headingSize="h5"
                    />
                    <TextArea
                      name={[CROP, "sanityNoteResidue"]}
                      initialValue={residueNote}
                      label={t("cropForm.residue.note")}
                      rules={[{ required: true, message: t("cropForm.errorMessages.residueNoteRequired") }]}
                      help={hasMixedResidueNotes ? t("cropForm.mixedValues") : undefined}
                      autoSize
                      disabled={isAnyCropConfirmed}
                    />
                  </Flex>
                )}
              </div>
            </Flex>
          );
        })()}
    </Flex>
  );
};
